<template>
  <div class="panel cc-panel">
    <p class="panel-heading">
      {{ $t("i18n.chartTitles.ccTemperatureOccurrence") }}
    </p>
    <div class="panel-block">
      <div :id="this.idChartDiv" class="cc-chart"></div>
    </div>
  </div>
</template>
<script>
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import * as am5exporting from '@amcharts/amcharts5/plugins/exporting'
import ccColors from '@/components/ccColors'

export default {
  name: 'ccTemperatureOccurrence',
  props: {
    idChartDiv: {
      type: String,
      required: true
    },
    data: {
      type: Array,
      required: true
    },
    noOfProcesses: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      occurrencesData: [],
      dataForChart: []
    }
  },
  mounted () {
    this.buildChart()
  },
  methods: {
    buildChart: function () {
      this.occurrencesData = []
      this.dataForChart = []

      // Loop each datapoint and aggregate/count hours per temperature
      this.data.forEach(dataPoint => {
        let currentTemperatureObject = false

        this.occurrencesData.forEach(temperatureObject => {
          if (temperatureObject.outdoorTemperatureRounded === dataPoint.outdoorTemperatureRounded) {
            currentTemperatureObject = temperatureObject
            temperatureObject.count++
          }
        })

        if (!currentTemperatureObject) {
          const temperatureObject = {
            outdoorTemperatureRounded: dataPoint.outdoorTemperatureRounded,
            count: 1
          }

          this.occurrencesData.push(temperatureObject)
        }
      })

      this.occurrencesData.sort((a, b) => a.outdoorTemperatureRounded - b.outdoorTemperatureRounded)

      // loop all temperatures that has a count and add temperatures not in interval
      const minTemp = this.occurrencesData[0].outdoorTemperatureRounded
      const maxTemp = this.occurrencesData[this.occurrencesData.length - 1].outdoorTemperatureRounded
      for (let tempToCount = minTemp; tempToCount <= maxTemp; tempToCount++) {
        const newTempCountElement = {
          outdoorTemperatureRounded: tempToCount,
          count: 0
        }

        // Check if temperature is in data
        const temperatureCountObject = this.occurrencesData.find(temperatureAggregate => temperatureAggregate.outdoorTemperatureRounded === tempToCount)

        // if so, use the count from that temp, but divide the temp by number of processes
        if (temperatureCountObject) {
          newTempCountElement.count = temperatureCountObject.count / this.noOfProcesses
        }

        // add to chart data
        this.dataForChart.push(newTempCountElement)
      }

      // Create root element
      am5.addLicense(this.$store.state.am5)
      const root = am5.Root.new(this.idChartDiv)

      // Create chart
      const chart = root.container.children.push(am5xy.XYChart.new(root, {
        interactive: false,
        layout: root.verticalLayout,
        paddingRight: 40,
        paddingTop: 0
      }))

      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: 'outdoorTemperatureRounded',
          renderer: am5xy.AxisRendererX.new(root, {
            minGridDistance: 15
          })
        })
      )

      xAxis.data.setAll(this.dataForChart)

      // Create axes
      const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {})
      }))

      // Add series
      const occurrenceSeries = chart.series.push(am5xy.ColumnSeries.new(root, {
        name: 'Occurrence per Temperature',
        xAxis: xAxis,
        yAxis: yAxis,
        categoryXField: 'outdoorTemperatureRounded',
        valueYField: 'count'
      }))

      occurrenceSeries.columns.template.setAll({
        stroke: ccColors.outdoorTemperature,
        fill: ccColors.outdoorTemperature
      })

      occurrenceSeries.data.setAll(this.dataForChart)

      // Add export menu
      am5exporting.Exporting.new(root, {
        menu: am5exporting.ExportingMenu.new(root, {})
      })

      // Add legend
      // https://www.amcharts.com/docs/v5/charts/xy-chart/legend-xy-series/
      const legend = chart.children.push(
        am5.Legend.new(root, {
          centerX: am5.percent(25),
          x: am5.percent(50),
          clickTarget: 'none' // Disable clickable legends
        })
      )

      legend.data.push(occurrenceSeries)

      // Add Labels to axises
      chart.children.unshift(am5.Label.new(root, {
        text: this.$t('i18n.global.numberOfHours'),
        fontWeight: '500',
        textAlign: 'center',
        y: am5.percent(-10),
        height: 0,
        paddingTop: 30
      }))

      chart.children.unshift(am5.Label.new(root, {
        text: '°C',
        fontWeight: '500',
        textAlign: 'left',
        x: am5.percent(100),
        y: am5.percent(81),
        paddingLeft: 10
      }))

      // Make stuff animate on load
      chart.appear(1000, 100)
    }
  }
}
</script>
